import React from 'react';
import { getFranklyCloudItems, uploadFranklyCloudImage, addFranklyCloudProImager, deleteFranklyCloudItem, getMyImagerScenes, getImagerBlob } from '../services/api';
import { renderSceneToCanvas } from '../utils/sceneRenderer';
import CloudUploadPanel, { UPLOAD_MAX_BYTES, UPLOAD_TYPES } from './CloudUploadPanel';

const DEV = process.env.NODE_ENV === 'development';
const MAX_CLOUD_ITEMS = 40;
const API_BASE = process.env.REACT_APP_API_URL || '';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');

const resolveUploadSrc = (src) => {
    if (!src || src.startsWith('data:') || src.startsWith('http')) return src;
    return `${API_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const makeId = () => `fc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const apiJoin = (base, p) => (base.endsWith('/') ? base : base + '/') + p.replace(/^\//, '');

const prox = (u) => {
    const s0 = String(u || '').trim();
    if (!s0 || s0.startsWith('data:') || s0.startsWith('/')) return s0;
    if (s0.includes('/api/imager/proxy?url=')) return s0;
    const s = /^https?:\/\//i.test(s0) ? s0 : `https://${s0}`;
    return apiJoin(API_BASE, `imager/proxy?url=${encodeURIComponent(s)}`);
};

const speechParts = {
    frnt: `${process.env.PUBLIC_URL}/img/spbb_frnt.png`,
    mid: `${process.env.PUBLIC_URL}/img/spbb_mid.png`,
    end: `${process.env.PUBLIC_URL}/img/spbb_end.png`,
};

const safeParseJson = (s) => {
    try { return JSON.parse(s); } catch { return null; }
};

const base64ToUint8 = (b64) => {
    try {
        const bin = atob(b64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        return u8;
    } catch { return new Uint8Array(0); }
};

const gunzipB64 = async (b64) => {
    try {
        if (typeof DecompressionStream === 'undefined') return atob(b64);
        const ds = new DecompressionStream('gzip');
        const stream = new Blob([base64ToUint8(b64)]).stream().pipeThrough(ds);
        return await new Response(stream).text();
    } catch { return atob(b64); }
};

const decodePerRowScene = async (row) => {
    try {
        const outer = safeParseJson(row.SceneJson);
        if (!outer || typeof outer !== 'object') return null;
        let sceneObj;
        if (outer.encoding === 'gzip+base64' && outer.blob) {
            const decompressed = await gunzipB64(outer.blob);
            sceneObj = safeParseJson(decompressed);
        } else {
            sceneObj = outer;
        }
        if (!sceneObj || typeof sceneObj !== 'object') return null;
        return { ...sceneObj, dbId: row.ID };
    } catch { return null; }
};

// Converts a canvas to a PNG data URL at its NATIVE resolution - no pixel scaling.
// The PNG format itself is deflate-compressed; no further resizing is done.
const canvasToPng = (sourceCanvas) => new Promise((resolve) => {
    sourceCanvas.toBlob(blob => {
        if (!blob) { resolve(sourceCanvas.toDataURL('image/png')); return; }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(sourceCanvas.toDataURL('image/png'));
        reader.readAsDataURL(blob);
    }, 'image/png');
});

// Converts a Uint8Array to a base64 string in chunks to avoid call-stack limits.
const uint8ToBase64 = (u8) => {
    let s = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < u8.length; i += CHUNK)
        s += String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK));
    return btoa(s);
};

// Gzip-compresses the raw PNG binary extracted from a data URL and returns
// { encoding: 'gzip+base64', blob } for lossless database storage.
// Falls back to { encoding: 'none', blob } if CompressionStream is unavailable.
const compressPngForStorage = async (dataUrl) => {
    const b64 = dataUrl.replace(/^data:image\/png;base64,/, '');
    if (typeof CompressionStream === 'undefined') return { encoding: 'none', blob: b64 };
    try {
        const binary = base64ToUint8(b64);
        const cs = new CompressionStream('gzip');
        const writer = cs.writable.getWriter();
        writer.write(binary);
        writer.close();
        const compressed = await new Response(cs.readable).arrayBuffer();
        return { encoding: 'gzip+base64', blob: uint8ToBase64(new Uint8Array(compressed)) };
    } catch {
        const b64fallback = dataUrl.replace(/^data:image\/png;base64,/, '');
        return { encoding: 'none', blob: b64fallback };
    }
};

// Decompresses a gzip+base64 blob back to a PNG data URL for display.
const decompressPngFromStorage = async (b64) => {
    const ds = new DecompressionStream('gzip');
    const stream = new Blob([base64ToUint8(b64)]).stream().pipeThrough(ds);
    const pngBuf = await new Response(stream).arrayBuffer();
    return `data:image/png;base64,${uint8ToBase64(new Uint8Array(pngBuf))}`;
};

// ─── PannableImage ────────────────────────────────────────────────────────────
// Natural-size image in a cropped container. Drag to pan; click fires onClick
// only if the pointer didn't move (drag detection threshold: 4px).

export const PannableImage = ({ src, alt, className, style, onClick }) => {
    const containerRef = React.useRef(null);
    const imgRef = React.useRef(null);
    const [offset, setOffset] = React.useState({ x: 0, y: 0 });
    const isDragging = React.useRef(false);
    const moved = React.useRef(false);
    const startPos = React.useRef({ x: 0, y: 0 });
    const startOffset = React.useRef({ x: 0, y: 0 });

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    const getClampBounds = () => {
        const c = containerRef.current;
        const img = imgRef.current;
        if (!c || !img) return { maxX: 0, maxY: 0 };
        const cw = c.offsetWidth;
        const ch = c.offsetHeight;
        const iw = img.naturalWidth || img.offsetWidth || 0;
        const ih = img.naturalHeight || img.offsetHeight || 0;
        return {
            maxX: Math.max(0, (iw - cw) / 2),
            maxY: Math.max(0, (ih - ch) / 2),
        };
    };

    const handlePointerDown = (e) => {
        isDragging.current = true;
        moved.current = false;
        startPos.current = { x: e.clientX, y: e.clientY };
        startOffset.current = { ...offset };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();
    };

    const handlePointerMove = (e) => {
        if (!isDragging.current) return;
        const dx = e.clientX - startPos.current.x;
        const dy = e.clientY - startPos.current.y;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
        const { maxX, maxY } = getClampBounds();
        setOffset({
            x: clamp(startOffset.current.x + dx, -maxX, maxX),
            y: clamp(startOffset.current.y + dy, -maxY, maxY),
        });
    };

    const handlePointerUp = (e) => {
        isDragging.current = false;
        if (!moved.current && typeof onClick === 'function') onClick(e);
    };

    return (
        <div
            ref={containerRef}
            className={`overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing select-none ${className || ''}`}
            style={style}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <img
                ref={imgRef}
                src={src}
                alt={alt || ''}
                style={{
                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                    maxWidth: 'none',
                    display: 'block',
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
                draggable={false}
            />
        </div>
    );
};

// ─── ImageLightbox ────────────────────────────────────────────────────────────
// Full-screen overlay with image at natural size. Drag to pan if oversized.
// Click backdrop, press ESC, or swipe down (touch) to close.
// Multi-image mode: pass items/index/onChange for left/right cycling.

export const ImageLightbox = ({ src, alt, onClose, items, index, onChange }) => {
    const currentSrc = items ? items[index]?.src : src;
    const currentAlt = items ? (items[index]?.name || items[index]?.alt || '') : (alt || '');

    const containerRef = React.useRef(null);
    const imgRef = React.useRef(null);
    const [offset, setOffset] = React.useState({ x: 0, y: 0 });
    const isDragging = React.useRef(false);
    const moved = React.useRef(false);
    const startPos = React.useRef({ x: 0, y: 0 });
    const startOffset = React.useRef({ x: 0, y: 0 });
    const startTime = React.useRef(0);

    const hasPrev = items && index > 0;
    const hasNext = items && index != null && index < items.length - 1;

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    const getClampBounds = () => {
        const c = containerRef.current;
        const img = imgRef.current;
        if (!c || !img) return { maxX: 0, maxY: 0 };
        const imgRect = img.getBoundingClientRect();
        return {
            maxX: Math.max(0, (imgRect.width - c.offsetWidth) / 2),
            maxY: Math.max(0, (imgRect.height - c.offsetHeight) / 2),
        };
    };

    React.useEffect(() => { setOffset({ x: 0, y: 0 }); }, [index]);

    React.useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && hasPrev && onChange) onChange(index - 1);
            if (e.key === 'ArrowRight' && hasNext && onChange) onChange(index + 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, hasPrev, hasNext, index, onChange]);

    const handlePointerDown = (e) => {
        moved.current = false;
        startPos.current = { x: e.clientX, y: e.clientY };
        startOffset.current = { ...offset };
        startTime.current = Date.now();
        if (e.target !== e.currentTarget) {
            isDragging.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
        }
    };

    const handlePointerMove = (e) => {
        if (!isDragging.current) return;
        const dx = e.clientX - startPos.current.x;
        const dy = e.clientY - startPos.current.y;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;
        const { maxX, maxY } = getClampBounds();
        setOffset({
            x: clamp(startOffset.current.x + dx, -maxX, maxX),
            y: clamp(startOffset.current.y + dy, -maxY, maxY),
        });
    };

    const handlePointerUp = (e) => {
        isDragging.current = false;
        const dy = e.clientY - startPos.current.y;
        const dx = e.clientX - startPos.current.x;
        const elapsed = Date.now() - startTime.current;
        if (e.pointerType === 'touch' && dy > 100 && Math.abs(dy) > Math.abs(dx) * 1.5 && elapsed < 500) {
            onClose();
        }
    };

    const handleInnerClick = (e) => {
        if (!moved.current && e.target === e.currentTarget) onClose();
    };

    const isOffset = offset.x !== 0 || offset.y !== 0;

    return (
        <div className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center p-4">
            <div
                ref={containerRef}
                className="relative w-full h-full overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onClick={handleInnerClick}
            >
                <img
                    ref={imgRef}
                    src={currentSrc}
                    alt={currentAlt}
                    style={{
                        transform: `translate(${offset.x}px, ${offset.y}px)`,
                        maxWidth: '100%',
                        maxHeight: '100%',
                        display: 'block',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                    draggable={false}
                />
            </div>
            <button
                type="button"
                onClick={onClose}
                className="absolute top-2 right-2 text-white/70 hover:text-white text-2xl leading-none z-10 w-8 h-8 flex items-center justify-center"
            >
                ✕
            </button>
            {isOffset && (
                <button
                    type="button"
                    onClick={() => setOffset({ x: 0, y: 0 })}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 bg-black/50 hover:bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full transition-opacity"
                >
                    Reset
                </button>
            )}
            {hasPrev && (
                <button
                    type="button"
                    onClick={() => onChange(index - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 w-10 h-10 flex items-center justify-center bg-black/40 rounded-full text-3xl leading-none select-none"
                >
                    ‹
                </button>
            )}
            {hasNext && (
                <button
                    type="button"
                    onClick={() => onChange(index + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 w-10 h-10 flex items-center justify-center bg-black/40 rounded-full text-3xl leading-none select-none"
                >
                    ›
                </button>
            )}
            {items && items.length > 1 && (
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2 z-10">
                    {items.map((_, i) => (
                        <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/40'}`} />
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── FranklyCloudModal ────────────────────────────────────────────────────────

/**
 * FranklyCloudModal
 *
 * Props:
 *   isOpen       - boolean
 *   onClose      - () => void
 *   userData     - user object (needs .username or .habboName)
 *   onInsert     - optional (item: {id,src,name,type,...}) => void
 *                  When provided, the modal shows in "insert" mode.
 */
const FranklyCloudModal = ({ isOpen, onClose, onInsert, userData, cloudUploadMode = 'all' }) => {
    const insertMode = typeof onInsert === 'function';

    // Real-time upload mode - starts from prop, updated live via WS broadcast
    const [liveUploadMode, setLiveUploadMode] = React.useState(cloudUploadMode);
    // null = unknown (WS not yet connected), true = connected, false = permanently down
    const [wsConnected, setWsConnected] = React.useState(null);

    React.useEffect(() => {
        setLiveUploadMode(cloudUploadMode);
    }, [cloudUploadMode]);

    React.useEffect(() => {
        const onMode = (e) => setLiveUploadMode(e.detail?.mode || 'offline');
        const onConnect = () => setWsConnected(true);
        const onDisconnect = () => setWsConnected(false);
        window.addEventListener('frankly:cloudUploadMode', onMode);
        window.addEventListener('frankly:wsConnected', onConnect);
        window.addEventListener('frankly:wsDisconnected', onDisconnect);
        return () => {
            window.removeEventListener('frankly:cloudUploadMode', onMode);
            window.removeEventListener('frankly:wsConnected', onConnect);
            window.removeEventListener('frankly:wsDisconnected', onDisconnect);
        };
    }, []);

    // If WS is confirmed down, disable uploads regardless of cached mode
    const effectiveMode = wsConnected === false ? 'offline' : liveUploadMode;
    const canUpload = effectiveMode === 'all'
        || (effectiveMode === 'staff_only' && (userData?.hasStaffRole || userData?.hasAdminRole));

    // view: 'cloud' | 'add-options' | 'proimager' | 'upload'
    const [view, setView] = React.useState('cloud');
    const [cloudItems, setCloudItems] = React.useState([]);
    const [cloudLoading, setCloudLoading] = React.useState(false);
    const [cloudMutating, setCloudMutating] = React.useState(false);

    // ProImager sub-state
    const [scenes, setScenes] = React.useState([]);
    const [scenesLoading, setScenesLoading] = React.useState(false);
    const [selectedSceneId, setSelectedSceneId] = React.useState(null);
    const sceneCachesRef = React.useRef(new Map());
    const [scenePreviews, setScenePreviews] = React.useState(new Map());
    const [sceneRendering, setSceneRendering] = React.useState(false);
    const [addingScene, setAddingScene] = React.useState(false);

    // Upload sub-state
    const [uploadFile, setUploadFile] = React.useState(null);
    const [uploadPreview, setUploadPreview] = React.useState(null);
    const [uploadDimensions, setUploadDimensions] = React.useState(null);
    const [uploadStatus, setUploadStatus] = React.useState('idle');
    // idle | validating | ready | uploading | done | rejected | error
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [uploadError, setUploadError] = React.useState('');
    const blobUrlRef = React.useRef(null);

    const [pendingDelete, setPendingDelete] = React.useState(null);

    const revokeBlobUrl = React.useCallback(() => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    }, []);

    React.useEffect(() => () => revokeBlobUrl(), [revokeBlobUrl]);

    const resetView = () => {
        setView('cloud');
        setSelectedSceneId(null);
        revokeBlobUrl();
        setUploadFile(null);
        setUploadPreview(null);
        setUploadDimensions(null);
        setUploadStatus('idle');
        setUploadProgress(0);
        setUploadError('');
        setPendingDelete(null);
    };

    // ── Load cloud on open ────────────────────────────────────────────────────
    React.useEffect(() => {
        if (!isOpen) return;
        resetView();
        setCloudLoading(true);
        getFranklyCloudItems()
            .then(async r => {
                const raw = Array.isArray(r?.items) ? r.items : [];
                const items = await Promise.all(raw.map(async (item) => {
                    if (item.type === 'upload') return { ...item, src: resolveUploadSrc(item.src) };
                    if (item.type === 'proimager' && item.encoding === 'gzip+base64' && item.blob) {
                        try {
                            const src = await decompressPngFromStorage(item.blob);
                            return { ...item, src };
                        } catch { return item; }
                    }
                    if (item.type === 'proimager' && item.encoding === 'none' && item.blob) {
                        return { ...item, src: `data:image/png;base64,${item.blob}` };
                    }
                    return item;
                }));
                setCloudItems(items);
            })
            .catch(() => setCloudItems([]))
            .finally(() => setCloudLoading(false));
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── ProImager: load scenes ────────────────────────────────────────────────
    const loadScenes = async () => {
        setScenesLoading(true);
        setScenes([]);
        try {
            const [perRowRes, blobRes] = await Promise.allSettled([getMyImagerScenes(), getImagerBlob()]);

            const perRowScenes = [];
            if (perRowRes.status === 'fulfilled' && perRowRes.value?.success) {
                const decoded = await Promise.all(
                    (perRowRes.value.scenes || []).filter(s => s.SceneJson).map(decodePerRowScene)
                );
                perRowScenes.push(...decoded.filter(Boolean));
            }

            const blobScenes = [];
            if (blobRes.status === 'fulfilled' && blobRes.value?.data?.blob) {
                try {
                    const json = await gunzipB64(blobRes.value.data.blob);
                    const payload = safeParseJson(json);
                    blobScenes.push(...(Array.isArray(payload?.scenes) ? payload.scenes : []));
                } catch {}
            }

            const perRowIds = new Set(perRowScenes.map(s => s.id));
            const merged = [
                ...perRowScenes,
                ...blobScenes.filter(s => !perRowIds.has(s.id)),
            ];

            setScenes(merged);
            if (DEV) console.log(`[FranklyCloud] Loaded ${merged.length} scenes`);
        } catch (err) {
            if (DEV) console.error('[FranklyCloud] Failed to load scenes:', err);
        } finally {
            setScenesLoading(false);
        }
    };

    const handleOpenProImager = () => {
        setView('proimager');
        if (scenes.length === 0) loadScenes();
    };

    const renderScene = React.useCallback(async (scene) => {
        if (!scene?.id) return;
        if (sceneCachesRef.current.has(scene.id)) {
            setScenePreviews(prev => new Map(prev).set(scene.id, sceneCachesRef.current.get(scene.id)));
            return;
        }
        setSceneRendering(true);
        try {
            const canvas = await renderSceneToCanvas(scene, {
                proxyUrl: prox,
                publicUrl: process.env.PUBLIC_URL,
                speechParts,
            });
            const dataUrl = await canvasToPng(canvas);
            sceneCachesRef.current.set(scene.id, dataUrl);
            setScenePreviews(prev => new Map(prev).set(scene.id, dataUrl));
        } catch (err) {
            if (DEV) console.error('[FranklyCloud] Scene render error:', err);
        } finally {
            setSceneRendering(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (view !== 'proimager' || !selectedSceneId) return;
        const scene = scenes.find(s => s.id === selectedSceneId);
        if (scene && !sceneCachesRef.current.has(scene.id)) renderScene(scene);
    }, [selectedSceneId, view, scenes, renderScene]);

    React.useEffect(() => {
        if (view !== 'proimager' || scenes.length === 0) return;
        let cancelled = false;
        const BATCH = 4;
        (async () => {
            const toRender = scenes.filter(s => !sceneCachesRef.current.has(s.id));
            for (let i = 0; i < toRender.length; i += BATCH) {
                if (cancelled) break;
                await Promise.all(toRender.slice(i, i + BATCH).map(s => renderScene(s)));
            }
        })();
        return () => { cancelled = true; };
    }, [view, scenes, renderScene]);

    const handleAddScene = async () => {
        if (!selectedSceneId) return;
        const preview = scenePreviews.get(selectedSceneId);
        const scene = scenes.find(s => s.id === selectedSceneId);
        if (!preview || !scene) return;

        if (cloudItems.length >= MAX_CLOUD_ITEMS) {
            alert(`Your FranklyCloud is full (max ${MAX_CLOUD_ITEMS} images). Remove some to add more.`);
            return;
        }

        setAddingScene(true);
        try {
            if (!sceneCachesRef.current.has(scene.id + '_stored')) {
                const canvas = await renderSceneToCanvas(scene, {
                    proxyUrl: prox,
                    publicUrl: process.env.PUBLIC_URL,
                    speechParts,
                });
                // Always PNG — never JPEG or any lossy format. No resize, no quality reduction.
                sceneCachesRef.current.set(scene.id + '_stored', await canvasToPng(canvas));
            }
            const dataUrl = sceneCachesRef.current.get(scene.id + '_stored') || preview;
            const name = scene.title || scene.name || `Scene ${cloudItems.length + 1}`;

            if (DEV) console.log('[FranklyCloud] Adding ProImager scene to cloud:', name);

            // Gzip the raw PNG binary for storage — lossless, never reduces quality.
            const { encoding, blob } = await compressPngForStorage(dataUrl);
            const res = await addFranklyCloudProImager({ name, encoding, blob });
            if (!res?.success) throw new Error(res?.message || 'Failed to save scene.');

            // Keep the decoded data URL in local state so the image shows immediately.
            setCloudItems(prev => [...prev, { id: res.itemId, type: 'proimager', name, src: dataUrl, addedAt: new Date().toISOString() }]);
            setView('cloud');
            setSelectedSceneId(null);
        } catch (err) {
            if (DEV) console.error('[FranklyCloud] Failed to add scene:', err);
        } finally {
            setAddingScene(false);
        }
    };

    // ── Upload flow ───────────────────────────────────────────────────────────
    const validateAndPreview = async (file) => {
        setUploadError('');
        setUploadStatus('validating');
        setUploadProgress(10);

        if (!UPLOAD_TYPES.includes(file.type)) {
            setUploadError('Only JPEG, PNG, GIF, and WebP images are accepted.');
            setUploadStatus('rejected');
            return;
        }

        if (file.size > UPLOAD_MAX_BYTES) {
            setUploadError(`Image is too large (max ${Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))}MB). Please use a smaller file.`);
            setUploadStatus('rejected');
            return;
        }

        revokeBlobUrl();
        const url = URL.createObjectURL(file);
        blobUrlRef.current = url;
        setUploadPreview(url);

        await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                setUploadDimensions({ w: img.naturalWidth, h: img.naturalHeight });
                setUploadFile(file);
                setUploadStatus('ready');
                resolve();
            };
            img.onerror = () => {
                setUploadError('Could not read image. Please try another file.');
                setUploadStatus('rejected');
                revokeBlobUrl();
                resolve();
            };
            img.src = url;
        });

        setUploadProgress(0);
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile || uploadStatus !== 'ready') return;
        if (cloudItems.length >= MAX_CLOUD_ITEMS) {
            setUploadError(`Your FranklyCloud is full (max ${MAX_CLOUD_ITEMS} images). Remove some first.`);
            return;
        }

        setUploadStatus('uploading');
        setUploadProgress(2);

        if (DEV) console.log('[FranklyCloud] Sending image for upload...', { name: uploadFile.name, size: uploadFile.size });

        let result;
        try {
            result = await uploadFranklyCloudImage(uploadFile, (fraction) => {
                setUploadProgress(Math.max(2, Math.round(fraction * 70)));
            });
            if (DEV) console.log('[FranklyCloud] Upload response:', result);
        } catch (err) {
            if (DEV) console.error('[FranklyCloud] Upload error:', err);
            setUploadError('Upload failed. Please check your connection and try again.');
            setUploadStatus('error');
            setUploadProgress(0);
            return;
        }

        if (!result?.success) {
            setUploadError(result?.reason || result?.message || 'Your image could not be uploaded.');
            setUploadStatus('rejected');
            setUploadProgress(0);
            if (DEV) console.log('[FranklyCloud] Upload rejected:', result);
            return;
        }

        setUploadProgress(95);
        if (DEV) console.log('[FranklyCloud] Image approved, row inserted by server.');
        revokeBlobUrl();
        setCloudItems(prev => [...prev, {
            id: result.itemId,
            type: 'upload',
            name: uploadFile.name.replace(/\.[^.]+$/, ''),
            src: resolveUploadSrc(result.src),
            filename: result.filename,
            width: result.width,
            height: result.height,
            bytes: result.bytes,
            addedAt: new Date().toISOString(),
        }]);

        setUploadProgress(100);
        setUploadStatus('done');

        setTimeout(() => {
            setView('cloud');
            setUploadFile(null);
            setUploadPreview(null);
            setUploadStatus('idle');
            setUploadProgress(0);
        }, 1200);
    };

    // ── Delete item ───────────────────────────────────────────────────────────
    const handleConfirmDelete = async (item) => {
        setCloudMutating(true);
        try {
            await deleteFranklyCloudItem(item.id);
            setCloudItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            if (DEV) console.error('[FranklyCloud] Delete failed:', err);
        } finally {
            setCloudMutating(false);
            setPendingDelete(null);
        }
    };

    // ── Insert into post ──────────────────────────────────────────────────────
    const handleInsertItem = (item) => {
        if (!insertMode) return;
        onInsert(item);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
            onMouseDown={(e) => { if (e.target === e.currentTarget) { resetView(); onClose(); } }}
        >
            <div className="bg-offwhite border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,0.4)] w-[720px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-black/20 bg-coal text-offwhite shrink-0">
                    <div className="flex items-center gap-2">
                        <img src="/img/franklycloud.png" alt="FranklyCloud" className="shrink-0" />
                        <span className="font-['Jersey_10'] text-2xl uppercase tracking-wide">
                            {view === 'proimager' ? 'Add from ProImager' : view === 'upload' ? 'Upload Image' : view === 'add-options' ? 'Add to FranklyCloud' : 'FranklyCloud'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {view !== 'cloud' && (
                            <button type="button" onClick={() => setView('cloud')} className="text-offwhite/60 hover:text-offwhite text-base transition-colors">
                                ← Back
                            </button>
                        )}
                        <button type="button" onClick={() => { resetView(); onClose(); }} className="text-offwhite hover:text-gold transition-colors text-xl leading-none">✕</button>
                    </div>
                </div>

                {/* ── CLOUD VIEW ─────────────────────────────────────────────────────── */}
                {view === 'cloud' && (
                    <div className="flex flex-col flex-1 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 border-b border-black/10 bg-black/[0.03] shrink-0">
                            <span className="text-xs text-coal/50 uppercase tracking-wider font-bold">
                                {cloudItems.length}/{MAX_CLOUD_ITEMS} images
                            </span>
                            <button
                                type="button"
                                onClick={() => setView('add-options')}
                                disabled={cloudItems.length >= MAX_CLOUD_ITEMS || cloudMutating}
                                className="px-3 py-1.5 bg-coal text-offwhite text-xs font-bold uppercase tracking-wide hover:bg-coal/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                + Add to FranklyCloud
                            </button>
                        </div>

                        <div className="max-h-[500px] overflow-y-auto habbo-scroll p-3">
                            {cloudLoading ? (
                                <div className="flex items-center justify-center h-32 text-coal/40 text-base">Loading…</div>
                            ) : cloudItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-40 gap-3 text-coal/40">
                                    <img src="/img/franklycloud.png" alt="" className="opacity-30" />
                                    <p className="text-base">Your cloud is empty. Add images to get started.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-5 gap-2">
                                    {cloudItems.map((item) => (
                                        <div key={item.id} className="relative group border border-black/20 bg-black/5 overflow-hidden">
                                            <div className="aspect-square flex items-center justify-center overflow-hidden bg-black/10">
                                                <img
                                                    src={item.type === 'upload' ? resolveUploadSrc(item.src) : item.src}
                                                    alt={item.name || 'Cloud image'}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            {pendingDelete !== item.id && (
                                                <button
                                                    type="button"
                                                    onClick={() => setPendingDelete(item.id)}
                                                    disabled={cloudMutating}
                                                    className="absolute top-1 right-1 bg-red-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                            <div className="px-1.5 py-1 border-t border-black/10 bg-offwhite">
                                                <p className="text-[11px] text-coal/60 truncate leading-tight">{item.name || '(unnamed)'}</p>
                                                <p className="text-[10px] text-coal/40 uppercase tracking-wide">{item.type === 'proimager' ? 'ProImager' : 'Upload'}</p>
                                                {insertMode && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleInsertItem(item)}
                                                        className="mt-1 w-full px-2 py-0.5 bg-gold text-coal text-[10px] font-bold uppercase"
                                                    >
                                                        Insert
                                                    </button>
                                                )}
                                            </div>
                                            {pendingDelete === item.id && (
                                                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-2 p-2">
                                                    <p className="text-white text-xs text-center font-bold">Remove from cloud?</p>
                                                    <div className="flex flex-col gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleConfirmDelete(item)}
                                                            disabled={cloudMutating}
                                                            className="px-2 py-1 bg-red-600 text-white text-xs font-bold uppercase"
                                                        >
                                                            Remove
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setPendingDelete(null)}
                                                            className="px-2 py-1 bg-offwhite text-coal text-xs font-bold uppercase"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {insertMode && cloudItems.length > 0 && (
                            <div className="px-4 py-2 border-t border-black/10 bg-black/[0.03] shrink-0">
                                <p className="text-xs text-coal/50 text-center">Click <strong>Insert</strong> below any image to add it to your post.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── ADD OPTIONS VIEW ───────────────────────────────────────────────── */}
                {view === 'add-options' && (
                    <div className="flex flex-col flex-1 items-center justify-center gap-4 p-8">
                        <p className="text-sm text-coal/50 text-center mb-2">How do you want to add an image?</p>
                        <button
                            type="button"
                            onClick={handleOpenProImager}
                            className="w-full max-w-[320px] px-4 py-4 border-2 border-black/20 bg-offwhite hover:bg-gold/20 hover:border-gold transition-colors text-left flex flex-col gap-1"
                        >
                            <span className="font-['Jersey_10'] text-xl uppercase tracking-wide text-coal">Add from ProImager</span>
                            <span className="text-sm text-coal/50">Save a scene from your ProImager as a cloud image.</span>
                        </button>
                        <div className={`w-full max-w-[320px] flex flex-col gap-1 ${!canUpload ? 'opacity-50' : ''}`}>
                            <button
                                type="button"
                                onClick={() => canUpload && setView('upload')}
                                disabled={!canUpload}
                                className={`w-full px-4 py-4 border-2 border-black/20 bg-offwhite text-left flex flex-col gap-1
                                    ${canUpload ? 'hover:bg-gold/20 hover:border-gold transition-colors' : 'cursor-not-allowed'}`}
                            >
                                <span className="font-['Jersey_10'] text-xl uppercase tracking-wide text-coal">Upload Image</span>
                                <span className="text-sm text-coal/50">
                                    {wsConnected === false
                                        ? 'Unable to verify upload status - please refresh the page.'
                                        : effectiveMode === 'offline'
                                            ? 'File uploads are currently disabled.'
                                            : effectiveMode === 'staff_only'
                                                ? 'Uploads are currently limited to staff members only.'
                                                : 'Upload a photo or image from your device.'}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* ── PROIMAGER VIEW ─────────────────────────────────────────────────── */}
                {view === 'proimager' && (
                    <div className="flex flex-col flex-1 overflow-hidden">
                        {scenesLoading ? (
                            <div className="flex items-center justify-center flex-1 text-coal/40 text-base">Loading your scenes…</div>
                        ) : scenes.length === 0 ? (
                            <div className="flex items-center justify-center flex-1 text-coal/40 text-base">No ProImager scenes found.</div>
                        ) : (
                            <>
                                <div className="max-h-[500px] overflow-y-auto habbo-scroll p-3 grid grid-cols-5 gap-2">
                                    {scenes.map((scene) => {
                                        const preview = scenePreviews.get(scene.id);
                                        const isSelected = selectedSceneId === scene.id;
                                        return (
                                            <button
                                                key={scene.id}
                                                type="button"
                                                onClick={() => setSelectedSceneId(scene.id)}
                                                className={`border-2 transition-colors overflow-hidden flex flex-col ${isSelected ? 'border-gold' : 'border-black/20 hover:border-black/50'}`}
                                            >
                                                <div className="aspect-video bg-black/10 flex items-center justify-center overflow-hidden">
                                                    {preview ? (
                                                        <img src={preview} alt={scene.title || scene.name || 'Scene'} className="w-full h-full object-contain" />
                                                    ) : (
                                                        <span className="text-[11px] text-coal/30">Rendering…</span>
                                                    )}
                                                </div>
                                                <div className="px-1.5 py-1 bg-offwhite border-t border-black/10">
                                                    <p className="text-[11px] text-coal/70 truncate leading-tight">{scene.title || scene.name || 'Untitled'}</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="px-4 py-3 border-t border-black/20 shrink-0 flex items-center justify-between gap-3">
                                    <p className="text-xs text-coal/50">
                                        {selectedSceneId ? `Selected: ${scenes.find(s => s.id === selectedSceneId)?.title || 'Scene'}` : 'Select a scene above'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleAddScene}
                                        disabled={!selectedSceneId || addingScene || sceneRendering}
                                        className="px-4 py-2 bg-coal text-offwhite text-sm font-bold uppercase tracking-wide hover:bg-coal/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {addingScene ? 'Saving…' : 'Add to Cloud'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── UPLOAD VIEW ────────────────────────────────────────────────────── */}
                {view === 'upload' && (
                    <CloudUploadPanel
                        status={uploadStatus}
                        preview={uploadPreview}
                        file={uploadFile}
                        dimensions={uploadDimensions}
                        progress={uploadProgress}
                        error={uploadError}
                        doneLabel="Added to your FranklyCloud!"
                        onFileChosen={validateAndPreview}
                        onReset={() => {
                            revokeBlobUrl();
                            setUploadFile(null);
                            setUploadPreview(null);
                            setUploadDimensions(null);
                            setUploadStatus('idle');
                            setUploadError('');
                            setUploadProgress(0);
                        }}
                        onSubmit={handleUploadSubmit}
                    />
                )}
            </div>
        </div>
    );
};

export default FranklyCloudModal;
